import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import {
  SERVICE_SCOPE,
  filterUnmatchedByScope,
  formatServiceSlotLabel,
  getNextServiceSlot,
  isUnmatchedBooking,
} from '../../utils/adminBookingFilters';

const getRiderName = (rider) => {
  if (!rider) return 'Unknown rider';
  const fullName = `${rider.firstName || ''} ${rider.lastName || ''}`.trim();
  return fullName || rider.email || 'Unknown rider';
};

const getPickupAddress = (booking) => {
  if (booking.pickupStreet || booking.pickupCity || booking.pickupPostcode) {
    return [
      booking.pickupStreet,
      [booking.pickupCity, booking.pickupPostcode].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ');
  }

  return booking.pickupLocation || 'Pickup location not provided';
};

const formatDate = (value) => {
  if (!value) return 'Date not specified';

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Date not specified';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  const safeText = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safeText.replace(/"/g, '""')}"`;
};

function UnmatchedRequests() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceScope, setServiceScope] = useState(SERVICE_SCOPE.NEXT_SERVICE);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(API_URLS.admin.bookings);
      if (!Array.isArray(response.data)) {
        throw new Error('The bookings response was not a list.');
      }
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching unmatched requests:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load unmatched requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const unmatchedBookings = useMemo(
    () => bookings.filter(isUnmatchedBooking),
    [bookings]
  );

  const nextServiceSlot = useMemo(
    () => getNextServiceSlot(unmatchedBookings),
    [unmatchedBookings]
  );

  const scopedBookings = useMemo(
    () => filterUnmatchedByScope(unmatchedBookings, serviceScope, nextServiceSlot),
    [unmatchedBookings, serviceScope, nextServiceSlot]
  );

  const visibleBookings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return scopedBookings;

    return scopedBookings.filter((booking) => [
      booking.id,
      getRiderName(booking.rider),
      booking.rider?.email,
      booking.rider?.phone,
      getPickupAddress(booking),
      booking.date,
      booking.serviceSession,
      booking.serviceTime,
    ].some((value) => String(value || '').toLowerCase().includes(term)));
  }, [scopedBookings, searchTerm]);

  const scopeLabel = useMemo(() => {
    if (serviceScope === SERVICE_SCOPE.NEXT_SERVICE) {
      return nextServiceSlot
        ? formatServiceSlotLabel(nextServiceSlot)
        : 'No upcoming unmatched requests';
    }
    if (serviceScope === SERVICE_SCOPE.UPCOMING) return 'All upcoming services';
    return 'All dates (including past)';
  }, [serviceScope, nextServiceSlot]);

  const exportCsv = () => {
    const headers = ['Booking ID', 'Rider', 'Email', 'Phone', 'Pickup Address', 'Date', 'Service', 'Service Time', 'Passengers', 'Special Requests'];
    const rows = visibleBookings.map((booking) => [
      booking.id,
      getRiderName(booking.rider),
      booking.rider?.email,
      booking.rider?.phone,
      getPickupAddress(booking),
      booking.date,
      booking.serviceSession,
      booking.serviceTime,
      booking.passengers,
      booking.specialRequests,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvCell).join(','))
      .join('\r\n');
    const scopeSuffix = serviceScope === SERVICE_SCOPE.NEXT_SERVICE ? 'next-service' : serviceScope;
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unmatched-requests-${scopeSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Unmatched Requests</h1>
          <p className="mt-2 text-sm text-gray-700">
            Pending bookings without an assigned driver, for transport team follow-up.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={fetchBookings}
            disabled={loading}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={visibleBookings.length === 0}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="mb-6 flex items-center justify-between rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{error}</span>
          <button type="button" onClick={fetchBookings} className="ml-4 font-medium underline">
            Try again
          </button>
        </div>
      )}

      <div className="mb-6 space-y-4 rounded-lg bg-white p-5 shadow">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Requests in current view</p>
            <p className="mt-1 text-3xl font-semibold text-gray-900">{visibleBookings.length}</p>
            <p className="mt-1 text-xs text-gray-500">
              {unmatchedBookings.length} total unmatched · {scopeLabel}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label htmlFor="service-scope" className="block text-sm font-medium text-gray-700">
                Service window
              </label>
              <select
                id="service-scope"
                value={serviceScope}
                onChange={(event) => setServiceScope(event.target.value)}
                className="mt-1 block w-full min-w-[220px] rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value={SERVICE_SCOPE.NEXT_SERVICE}>Next service only</option>
                <option value={SERVICE_SCOPE.UPCOMING}>All upcoming</option>
                <option value={SERVICE_SCOPE.ALL}>All (incl. past)</option>
              </select>
            </div>
            <div className="relative w-full sm:max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search rider, phone, address, or booking ID"
                aria-label="Search unmatched requests"
                className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
        {serviceScope === SERVICE_SCOPE.NEXT_SERVICE && nextServiceSlot && (
          <p className="rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
            <span className="font-medium">Next service:</span> {formatServiceSlotLabel(nextServiceSlot)}
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" aria-label="Loading unmatched requests" />
          </div>
        ) : visibleBookings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-gray-900">
              {scopedBookings.length === 0
                ? (serviceScope === SERVICE_SCOPE.NEXT_SERVICE && unmatchedBookings.length > 0
                  ? 'No unmatched requests for the next service.'
                  : 'There are no pending bookings waiting for a driver.')
                : 'No requests match your search.'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {scopedBookings.length === 0 && serviceScope === SERVICE_SCOPE.NEXT_SERVICE && unmatchedBookings.length > 0
                ? 'Try “All upcoming” or “All” to see other dates.'
                : 'New unmatched bookings will appear here.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {visibleBookings.map((booking) => {
              const rider = booking.rider;
              const phone = rider?.phone;

              return (
                <li key={booking.id} className="p-5 sm:p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-400" />
                        <div className="min-w-0">
                          <h2 className="truncate text-base font-semibold text-gray-900">{getRiderName(rider)}</h2>
                          <p className="text-sm text-gray-500">Booking #{booking.id}</p>
                        </div>
                        <span className="ml-auto inline-flex flex-shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                          Pending match
                        </span>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <div className="flex items-start gap-2 text-sm text-gray-700">
                          <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          <span>{getPickupAddress(booking)}</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm text-gray-700">
                          <CalendarIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          <span>
                            {formatDate(booking.date)}
                            {(booking.serviceSession || booking.serviceTime) &&
                              ` · ${booking.serviceSession || ''}${booking.serviceTime ? ` ${booking.serviceTime}` : ''}`}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {rider?.email || 'Email not provided'}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <PhoneIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          {phone ? (
                            <a className="font-medium text-indigo-700 hover:text-indigo-900" href={`tel:${phone}`}>
                              {phone}
                            </a>
                          ) : (
                            <span>Phone not provided</span>
                          )}
                        </div>
                      </div>

                      {booking.specialRequests && (
                        <p className="mt-3 text-sm text-gray-600">
                          <span className="font-medium">Special requests:</span> {booking.specialRequests}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default UnmatchedRequests;
